import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

import { useMediaObjectUrl } from 'src/hooks/use-media-object-url';

import { MEDIA_PLACEHOLDER } from 'src/utils/media';

import Image from 'src/components/image';

// ----------------------------------------------------------------------

/**
 * Imagen de un documento servido por el BACKEND.
 *
 * Sustituye a `<Image src={`data:image/jpeg;base64,${media.file}`} />`: el binario ya no
 * viaja dentro del JSON (queda NULL tras el backfill al almacen de objetos), asi que se
 * pide al endpoint de streaming.
 *
 * Cuando la vista conoce el paciente dueno hay que pasar `patientId`: esa es la ruta que
 * comprueba pertenencia en el backend.
 *
 * Si el backend responde 404 (no existe, borrado, sin permiso o sin pertenencia) se pinta
 * el placeholder, nunca una imagen rota.
 */
export default function MediaImage({
  media,
  patientId,
  alt = '',
  ratio,
  sx,
  placeholderSrc = MEDIA_PLACEHOLDER,
  ...other
}) {
  const { url, loading, error } = useMediaObjectUrl(media, { patientId });

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{ borderRadius: 2, width: 1, height: 200, ...sx }}
      />
    );
  }

  if (error || !url) {
    return (
      <Box
        component="img"
        alt={alt || 'Documento no disponible'}
        src={placeholderSrc}
        sx={{
          width: 1,
          objectFit: 'contain',
          borderRadius: 2,
          bgcolor: 'background.neutral',
          p: 3,
          ...sx,
        }}
      />
    );
  }

  return <Image alt={alt} src={url} ratio={ratio} sx={sx} {...other} />;
}

MediaImage.propTypes = {
  /** mediaId (string) o el objeto MediaBlob/MediaResponseDto devuelto por el backend. */
  media: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Paciente dueno del documento, cuando la vista esta en su contexto. */
  patientId: PropTypes.string,
  alt: PropTypes.string,
  ratio: PropTypes.string,
  sx: PropTypes.object,
  placeholderSrc: PropTypes.string,
};
